/**
 * "Eğitim ve SOP Takibi" bölümünün gövdesi.
 *
 * Veriyi hesaplamaz — `useTeamTraining` çıktısını gösterir. Bölüm başlığı ve
 * aç/kapa düğmesi diğer bölümlerle aynı görünsün diye üst sayfada kalır.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { LuBookOpen, LuChevronDown, LuCircleCheck, LuDownload, LuGraduationCap, LuLoaderCircle, LuRefreshCw, LuTriangleAlert, LuUserRoundX } from 'react-icons/lu';
import * as XLSX from 'xlsx';
import { parseDueDate } from '../../utils/assignmentDeadlines';
import Button from '../../components/ui/Button';
import { HelpNote } from '../ProfilePage/components/ProfileUi';
import useQualityText from '../QualityManagementPage/kit/qualityText';
import useSmoText from './smoText';
import useActivityFormat from './parts/activityFormat';
import { PanelTile } from './parts/PanelTile';

// Excel stays Turkish (like every export in the portal)
const formatDueDate = (value) => {
  const date = parseDueDate(value);
  return date ? date.toLocaleDateString('tr-TR') : '—';
};

const TeamTrainingPanel = ({ data, smoName }) => {
  const { label, locale } = useSmoText();
  const { groupLabel } = useQualityText();
  const format = useActivityFormat();
  const { rows, summary, loading, error, reload } = data;
  const [expandedId, setExpandedId] = useState(null);

  const dueText = (value) => {
    const date = parseDueDate(value);
    return date ? date.toLocaleDateString(locale) : '—';
  };
  const typeText = (type) => (type === 'SOP' ? 'SOP' : label('training.education'));

  const downloadExcel = () => {
    const sheet = XLSX.utils.json_to_sheet(
      rows.flatMap((row) =>
        row.items.map((item) => ({
          'Çalışan': row.name,
          'Rol': row.role,
          'Tür': item.type,
          'Atama': item.name,
          'Son Tarih': formatDueDate(item.dueDate),
          'Durum': item.deadlineLabel,
        }))
      )
    );
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Eğitim ve SOP');
    XLSX.writeFile(book, `egitim-sop-takibi_${(smoName || 'smo').replace(/\s+/g, '-')}.xlsx`);
  };

  if (loading && rows.length === 0) {
    return (
      <p className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-8 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
        <LuLoaderCircle aria-hidden="true" className="tw-h-5 tw-w-5 tw-animate-spin tw-text-teal-500" />
        {label('training.loading')}
      </p>
    );
  }

  if (error) {
    return <HelpNote tone="danger">{label('training.error')}</HelpNote>;
  }

  if (rows.length === 0) {
    return (
      <p className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-8 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
        <LuCircleCheck aria-hidden="true" className="tw-h-5 tw-w-5 tw-text-emerald-500" />
        {label('training.allDone')}
      </p>
    );
  }

  return (
    <div className="tw-space-y-4">
      <div className="tw-grid tw-grid-cols-2 tw-gap-3 lg:tw-grid-cols-4">
        <PanelTile icon={LuUserRoundX} value={summary.pendingPeople} of={summary.teamSize} title={label('training.tiles.pendingPeople')} />
        <PanelTile icon={LuBookOpen} value={summary.pendingSops} title={label('training.tiles.pendingSops')} />
        <PanelTile icon={LuGraduationCap} value={summary.pendingEducations} title={label('training.tiles.pendingEducations')} />
        <PanelTile icon={LuTriangleAlert} value={summary.overdue} title={label('training.tiles.overdue')} alert={summary.overdue > 0} />
      </div>

      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <span className="tw-min-w-0 tw-flex-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('training.note')}</span>
        <Button size="sm" onClick={reload} disabled={loading}>
          <LuRefreshCw aria-hidden="true" className={`tw-h-4 tw-w-4 ${loading ? 'tw-animate-spin' : ''}`} />
          {label('header.refresh')}
        </Button>
        <Button size="sm" variant="success" onClick={downloadExcel}>
          <LuDownload aria-hidden="true" className="tw-h-4 tw-w-4" />
          Excel
        </Button>
      </div>

      <div className="tw-relative tw-overflow-x-auto tw-rounded-2xl tw-border tw-border-slate-200/80 glass-scrollbar dark:tw-border-white/10">
        <table className="tw-w-full tw-border-collapse tw-text-sm">
          <thead className="tw-bg-slate-50/90 dark:tw-bg-white/[0.03]">
            <tr>
              {['worker', 'role', 'pendingSop', 'pendingEducation', 'nextDeadline'].map((key) => (
                <th key={key} scope="col" className="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-text-left tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 dark:tw-text-slate-400">{label(`training.columns.${key}`)}</th>
              ))}
              <th scope="col" className="tw-w-8"><span className="tw-sr-only">{label('table.expand')}</span></th>
            </tr>
          </thead>
          <tbody className="tw-divide-y tw-divide-slate-200/70 tw-text-slate-700 dark:tw-divide-white/[0.06] dark:tw-text-slate-200">
            {rows.map((row) => {
              const expanded = expandedId === row.id;
              return (
                <React.Fragment key={row.id}>
                  <tr onClick={() => setExpandedId(expanded ? null : row.id)} aria-expanded={expanded} className={`tw-cursor-pointer tw-transition-colors ${expanded ? 'tw-bg-sky-500/[0.06]' : 'hover:tw-bg-slate-900/[0.02] dark:hover:tw-bg-white/[0.03]'}`}>
                    <td className="tw-px-4 tw-py-2.5">
                      <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                        <span className="tw-whitespace-nowrap tw-font-semibold tw-text-slate-900 dark:tw-text-white">{row.name}</span>
                        {row.overdueCount > 0 && (
                          <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-rose-500/15 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-rose-700 dark:tw-text-rose-300">
                            <LuTriangleAlert aria-hidden="true" className="tw-h-3 tw-w-3" />
                            {label('training.overdueBadge', { count: row.overdueCount })}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="tw-whitespace-nowrap tw-px-4 tw-py-2.5 tw-text-slate-500 dark:tw-text-slate-400">{groupLabel(row.role)}</td>
                    <td className="tw-px-4 tw-py-2.5 tw-tabular-nums">{row.sopCount || '—'}</td>
                    <td className="tw-px-4 tw-py-2.5 tw-tabular-nums">{row.educationCount || '—'}</td>
                    <td className={`tw-whitespace-nowrap tw-px-4 tw-py-2.5 ${row.nextDeadline?.overdue ? 'tw-font-semibold tw-text-rose-600 dark:tw-text-rose-300' : ''}`}>
                      {row.nextDeadline ? format.deadline(row.nextDeadline.dueDate) : label('training.noDeadline')}
                    </td>
                    <td className="tw-px-2 tw-py-2.5 tw-text-slate-400">
                      <LuChevronDown aria-hidden="true" className={`tw-h-4 tw-w-4 tw-transition-transform ${expanded ? 'tw-rotate-180' : ''}`} />
                    </td>
                  </tr>

                  {expanded && (
                    <tr className="tw-bg-slate-50/70 dark:tw-bg-white/[0.02]">
                      <td colSpan={6} className="tw-whitespace-normal tw-px-4 tw-py-3">
                        <ul className="tw-space-y-1.5">
                          {row.items.map((item) => (
                            <li key={`${item.type}-${item.id}`} className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-slate-200/80 tw-bg-white/80 tw-px-3 tw-py-2 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                              <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold ${item.type === 'SOP' ? 'tw-bg-violet-500/15 tw-text-violet-800 dark:tw-text-violet-200' : 'tw-bg-amber-400/20 tw-text-amber-800 dark:tw-text-amber-200'}`}>
                                {item.type === 'SOP' ? <LuBookOpen aria-hidden="true" className="tw-h-3 tw-w-3" /> : <LuGraduationCap aria-hidden="true" className="tw-h-3 tw-w-3" />}
                                {typeText(item.type)}
                              </span>
                              <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-text-slate-800 dark:tw-text-slate-100" title={item.name}>{item.name}</span>
                              <span className={`tw-text-xs ${item.overdue ? 'tw-font-semibold tw-text-rose-600 dark:tw-text-rose-300' : 'tw-text-slate-500 dark:tw-text-slate-400'}`}>
                                {format.deadline(item.dueDate)}
                                {item.dueDate && <small> · {dueText(item.dueDate)}</small>}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

TeamTrainingPanel.propTypes = {
  data: PropTypes.object.isRequired,
  smoName: PropTypes.string,
};

export default TeamTrainingPanel;
